import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DIAGNOSIS_CERTAINTY_LABELS, formatEatTime } from '@wodi/domain';
import {
  DIAGNOSIS_CODE_SYSTEM_KEYS,
  type DiagnosisView,
  type TerminologyCoding,
  type WorkspaceView,
} from '@wodi/shared';
import { Button, Kbd, Notice, RadioGroup, Section, Tag, TextField, useShortcut } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  correctDiagnosis,
  diagnosisEnteredInError,
  recordDiagnosis,
  recordProblem,
} from '../api/consultation.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { TerminologyPicker } from '../terminology/terminology-picker.tsx';
import { toasts } from '../toasts.ts';
import styles from './consultation.module.css';

// Diagnoses on the visit (plan §7.1): ICD-11 through the coded-value picker, primary or
// secondary, provisional or confirmed, with the clinician's own words beside the code.
// A change is a correction with a reason; nothing is edited in place.

type Rank = DiagnosisView['rank'];
type Certainty = DiagnosisView['certainty'];

const RANK_LABELS: Record<Rank, string> = { primary: 'Primary', secondary: 'Secondary' };

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function diagnosisTitle(d: Pick<DiagnosisView, 'coding' | 'clinicianText'>): string {
  return d.coding?.display ?? d.clinicianText ?? '';
}

function AddDiagnosis({ view, onDone }: { view: WorkspaceView; onDone: () => void }) {
  const queryClient = useQueryClient();
  const hasPrimary = view.diagnoses.some((d) => d.status === 'current' && d.rank === 'primary');
  const [coding, setCoding] = useState<TerminologyCoding | null>(null);
  const [words, setWords] = useState('');
  const [rank, setRank] = useState<Rank>(hasPrimary ? 'secondary' : 'primary');
  const [certainty, setCertainty] = useState<Certainty>('provisional');
  const add = useMutation({
    mutationFn: () =>
      recordDiagnosis(view.visit.id, {
        coding,
        clinicianText: words.trim() === '' ? null : words.trim(),
        rank,
        certainty,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['visits', view.visit.id, 'workspace'], next);
      toasts.show({ title: 'Diagnosis recorded' });
      onDone();
    },
  });
  return (
    <div className={styles.form}>
      {coding === null ? (
        <TerminologyPicker
          label="Diagnosis (ICD-11)"
          systems={DIAGNOSIS_CODE_SYSTEM_KEYS}
          onSelect={setCoding}
          autoFocus
        />
      ) : (
        <p className={styles.itemTitle}>
          <span className={`wodi-id ${styles.code}`}>{coding.code}</span>
          {coding.display}{' '}
          <Button
            dense
            variant="quiet"
            onPress={() => {
              setCoding(null);
            }}
          >
            Change code
          </Button>
        </p>
      )}
      <TextField
        label="Your words (optional)"
        description="Laterality, '?early' and the like. Without a code, only while ICD-11 isn't set up."
        value={words}
        onChange={setWords}
      />
      <RadioGroup
        label="Rank"
        orientation="horizontal"
        value={rank}
        onChange={(v) => {
          setRank(v as Rank);
        }}
        options={(['primary', 'secondary'] as const).map((r) => ({ id: r, label: RANK_LABELS[r] }))}
      />
      <RadioGroup
        label="Certainty"
        orientation="horizontal"
        value={certainty}
        onChange={(v) => {
          setCertainty(v as Certainty);
        }}
        options={(['provisional', 'confirmed'] as const).map((c) => ({
          id: c,
          label: DIAGNOSIS_CERTAINTY_LABELS[c],
        }))}
      />
      {add.error === null ? null : (
        <Notice tone="error">{errorText(add.error, 'The diagnosis could not be recorded.')}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={coding === null && words.trim() === ''}
          isPending={add.isPending}
          pendingLabel="Recording…"
          onPress={() => {
            add.mutate();
          }}
        >
          Record diagnosis
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function ChangeDialog({
  view,
  diagnosis,
  onClose,
}: {
  view: WorkspaceView;
  diagnosis: DiagnosisView;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [rank, setRank] = useState<Rank>(diagnosis.rank);
  const [certainty, setCertainty] = useState<Certainty>(diagnosis.certainty);
  const change = useMutation({
    mutationFn: (reason: string) => correctDiagnosis(diagnosis.id, { rank, certainty, reason }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id, 'workspace'] });
      toasts.show({ title: 'Diagnosis changed' });
      onClose();
    },
  });
  return (
    <ReasonDialog
      title={`Change ${diagnosisTitle(diagnosis)}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      confirmLabel="Change diagnosis"
      pendingLabel="Changing…"
      isPending={change.isPending}
      error={change.error === null ? null : errorText(change.error, 'It could not be changed.')}
      onConfirm={(reason) => {
        change.mutate(reason);
      }}
    >
      <RadioGroup
        label="Rank"
        orientation="horizontal"
        value={rank}
        onChange={(v) => {
          setRank(v as Rank);
        }}
        options={(['primary', 'secondary'] as const).map((r) => ({ id: r, label: RANK_LABELS[r] }))}
      />
      <RadioGroup
        label="Certainty"
        orientation="horizontal"
        value={certainty}
        onChange={(v) => {
          setCertainty(v as Certainty);
        }}
        options={(['provisional', 'confirmed', 'refuted'] as const).map((c) => ({
          id: c,
          label: DIAGNOSIS_CERTAINTY_LABELS[c],
        }))}
      />
      <p className={styles.help}>The earlier entry stays in the history with your reason.</p>
    </ReasonDialog>
  );
}

export function DiagnosesPanel({ view, mayWrite }: { view: WorkspaceView; mayWrite: boolean }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [changing, setChanging] = useState<DiagnosisView | null>(null);
  const [erroring, setErroring] = useState<DiagnosisView | null>(null);
  const open = view.visit.status === 'open';
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id, 'workspace'] });
  const markError = useMutation({
    mutationFn: (reason: string) => diagnosisEnteredInError(erroring?.id ?? '', reason),
    onSuccess: async () => {
      setErroring(null);
      await refresh();
      toasts.show({ title: 'Marked as entered in error' });
    },
  });
  const toProblems = useMutation({
    mutationFn: (d: DiagnosisView) =>
      recordProblem(view.visit.id, { coding: null, fromDiagnosisId: d.id }),
    onSuccess: (next) => {
      queryClient.setQueryData(['visits', view.visit.id, 'workspace'], next);
      toasts.show({ title: 'Added to problem list' });
    },
  });
  useShortcut(
    'alt+d',
    () => {
      if (mayWrite && open) setAdding(true);
    },
    { description: 'Add diagnosis' },
  );
  const current = view.diagnoses.filter((d) => d.status === 'current');
  const earlier = view.diagnoses.filter((d) => d.status !== 'current');
  const onList = new Set(view.problems.map((p) => p.fromDiagnosisId));

  return (
    <Section title="Diagnoses">
      {current.length === 0 ? <p className={styles.help}>No diagnosis recorded yet.</p> : null}
      <ul className={styles.list}>
        {current
          .slice()
          .sort((a, b) => Number(b.rank === 'primary') - Number(a.rank === 'primary'))
          .map((d) => (
            <li key={d.id} className={styles.item}>
              <span className={styles.itemMain}>
                <span className={styles.itemTitle}>
                  {d.coding === null ? null : (
                    <span className={`wodi-id ${styles.code}`}>{d.coding.code}</span>
                  )}
                  {diagnosisTitle(d)}
                </span>
                <span className={styles.itemMeta}>
                  {[
                    RANK_LABELS[d.rank],
                    DIAGNOSIS_CERTAINTY_LABELS[d.certainty],
                    d.coding === null ? 'Not coded' : null,
                    d.coding !== null && d.clinicianText !== null ? d.clinicianText : null,
                    `${formatEatTime(new Date(d.diagnosedAt))} by ${d.recordedBy.displayName}`,
                  ]
                    .filter((x) => x !== null)
                    .join(' · ')}
                </span>
              </span>
              {mayWrite && open ? (
                <span className={styles.actions}>
                  <Button
                    dense
                    variant="quiet"
                    onPress={() => {
                      setChanging(d);
                    }}
                  >
                    Change
                  </Button>
                  {onList.has(d.id) ? (
                    <Tag state="done">On problem list</Tag>
                  ) : (
                    <Button
                      dense
                      variant="quiet"
                      isPending={toProblems.isPending && toProblems.variables.id === d.id}
                      pendingLabel="Adding…"
                      onPress={() => {
                        toProblems.mutate(d);
                      }}
                    >
                      Add to problem list
                    </Button>
                  )}
                  <Button
                    dense
                    variant="quiet"
                    onPress={() => {
                      setErroring(d);
                    }}
                  >
                    Entered in error
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
      </ul>
      {toProblems.error === null ? null : (
        <Notice tone="error">{errorText(toProblems.error, 'It could not be added.')}</Notice>
      )}
      {mayWrite && open ? (
        adding ? (
          <AddDiagnosis
            view={view}
            onDone={() => {
              setAdding(false);
            }}
          />
        ) : (
          <div className={styles.actions}>
            <Button
              shortcut="alt+d"
              onPress={() => {
                setAdding(true);
              }}
            >
              Add diagnosis
            </Button>
            <Kbd combo="alt+d" />
          </div>
        )
      ) : null}
      {earlier.length === 0 ? null : (
        <details>
          <summary>{`Earlier entries (${String(earlier.length)})`}</summary>
          <ul className={styles.list}>
            {earlier.map((d) => (
              <li key={d.id} className={styles.item} data-inactive>
                <span className={styles.itemMain}>
                  <span>{diagnosisTitle(d)}</span>
                  <span className={styles.itemMeta}>
                    {`${d.status === 'corrected' ? 'Changed' : 'Entered in error'}: ${d.statusReason ?? ''}`}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {changing === null ? null : (
        <ChangeDialog
          view={view}
          diagnosis={changing}
          onClose={() => {
            setChanging(null);
          }}
        />
      )}
      <ReasonDialog
        title="Mark the diagnosis as entered in error"
        isOpen={erroring !== null}
        onOpenChange={(o) => {
          if (!o) setErroring(null);
        }}
        confirmLabel="Mark as entered in error"
        pendingLabel="Marking…"
        isPending={markError.isPending}
        error={
          markError.error === null ? null : errorText(markError.error, 'It could not be marked.')
        }
        onConfirm={(reason) => {
          markError.mutate(reason);
        }}
      >
        <p>Nothing is deleted: the entry stays in the history, marked with your reason.</p>
      </ReasonDialog>
    </Section>
  );
}
